import { DatePicker, Flex, Form, Select, InputNumber } from 'antd';
import { FormInstance } from 'antd/es/form/hooks/useForm';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { FULL_LOCALIZED_DATE_AND_TIME_FORMAT } from 'technical/string/formatter';
import { notEmpty } from 'technical/utils/not-empty';
import { DecimalInput } from 'ui/form/decimal-input';
import { DurationInput } from 'ui/form/duration-input';

import { UpdateRingFormValues } from './types';

interface UpdateRingFormProps {
  form: FormInstance<UpdateRingFormValues>;
  save: (row: UpdateRingFormValues) => void;
}

export const UpdateRingForm = ({ form, save }: UpdateRingFormProps) => {
  const { t } = useTranslation();

  const { currentConstructionSite } = useAppContext();

  const editableLength =
    notEmpty(currentConstructionSite?.ringLengthCode0) &&
    notEmpty(currentConstructionSite?.ringLengthCode1);

  const lengthOptions = editableLength
    ? [
        {
          label: currentConstructionSite?.ringLengthCode0,
          value: 0,
        },
        {
          label: currentConstructionSite?.ringLengthCode1,
          value: 1,
        },
      ]
    : [];

  return (
    <Form<UpdateRingFormValues> form={form} layout="vertical" onFinish={save}>
      <Form.Item<UpdateRingFormValues>
        name="keyPosition"
        label={t('productionAndPerformance.ring.table.keyPosition')}
      >
        <InputNumber
          min={1}
          prefix={currentConstructionSite?.keyPositionPrefix}
          max={currentConstructionSite?.nbKeyPosition ?? undefined}
          placeholder={t('input.keyPosition.placeholder')}
        />
      </Form.Item>
      <Form.Item<UpdateRingFormValues>
        name="length"
        label={t('productionAndPerformance.ring.table.length')}
      >
        <Select options={lengthOptions} disabled={!editableLength} allowClear />
      </Form.Item>
      <Form.Item<UpdateRingFormValues>
        name="buildEndMetricPoint"
        label={t('productionAndPerformance.ring.table.buildEndMetricPoint')}
      >
        <DecimalInput
          min={0}
          style={{ width: '100%' }}
          step={0.001}
          decimalLimit={3}
        />
      </Form.Item>

      <Flex gap={8}>
        <Form.Item<UpdateRingFormValues>
          name="excavationStart"
          label={t('productionAndPerformance.ring.table.excavationStart')}
          style={{ flex: 1 }}
        >
          <DatePicker
            style={{ width: '100%' }}
            showTime={{ format: 'LT' }}
            format={FULL_LOCALIZED_DATE_AND_TIME_FORMAT}
            allowClear
          />
        </Form.Item>
        <Form.Item<UpdateRingFormValues>
          name="excavationEnd"
          label={t('productionAndPerformance.ring.table.excavationEnd')}
          style={{ flex: 1 }}
        >
          <DatePicker
            style={{ width: '100%' }}
            showTime={{ format: 'LT' }}
            format={FULL_LOCALIZED_DATE_AND_TIME_FORMAT}
            allowClear
          />
        </Form.Item>
      </Flex>
      <Form.Item<UpdateRingFormValues>
        name="realExcavationDuration"
        label={t('productionAndPerformance.ring.table.realExcavationDuration')}
      >
        <DurationInput allowClear />
      </Form.Item>

      <Flex gap={8}>
        <Form.Item<UpdateRingFormValues>
          style={{ flex: 1 }}
          name="buildStart"
          label={t('productionAndPerformance.ring.table.buildStart')}
        >
          <DatePicker
            style={{ width: '100%' }}
            showTime={{ format: 'LT' }}
            format={FULL_LOCALIZED_DATE_AND_TIME_FORMAT}
            allowClear
          />
        </Form.Item>
        <Form.Item<UpdateRingFormValues>
          style={{ flex: 1 }}
          name="buildEnd"
          label={t('productionAndPerformance.ring.table.buildEnd')}
        >
          <DatePicker
            style={{ width: '100%' }}
            showTime={{ format: 'LT' }}
            format={FULL_LOCALIZED_DATE_AND_TIME_FORMAT}
            allowClear
          />
        </Form.Item>
      </Flex>
      <Form.Item
        name={['realBuildDuration']}
        label={t('productionAndPerformance.ring.table.realBuildDuration')}
      >
        <DurationInput allowClear />
      </Form.Item>
    </Form>
  );
};
